import { Button, Form, Input, Modal, Select, Switch, Table, Tooltip } from "antd";
import { useEffect, useState } from "react";
import { CloseOutlined, InfoCircleOutlined } from "@ant-design/icons";
import ApiService from "../../Api.service";
import ApiUrls, { displayError } from "../../ApiUrls";
import { openNotification } from "../MainLayout/Notification";
import "./Users.css";
import FiltersModal from "../Advanced Filters/FiltersModal";
import { useHistory } from "react-router-dom";

export const displayRoles = {
    "ADMIN": "Admin",
    "HELP_DESK": "Help Desk",
    "READ_ONLY": "Read-only Admin",
    "USER": "User"
}

export function Hint({ title }) {
    return <Tooltip placement="right" title={title}>
        <InfoCircleOutlined style={{ padding: '0 4px 0 4px', top: "1px", position: "relative" }} />
    </Tooltip>
}

const initialValues = {
    first_name: '',
    last_name: '',
    display_name: '',
    idp_user_name: '',
    email: '',
    account_uids: [],
    create_user_from_existing: true
};

export const displayFields = {
    account_uids: "Accounts",
    first_name: 'First Name',
    last_name: 'Last Name',
    display_name: 'Display Name',
    idp_user_name: 'Username',
    email: 'Email',
    account_name: "Account Name",
    status: "Status",
    create_user_from_existing: "Use Existing User Details"
};

const setPageAndPageSize = (setPage, page, setPageSize, pageSize) => {
    setPage(page);
    setPageSize(pageSize);
};

function Users() {
    const history = useHistory();
    const [loadingDetails, setLoadingDetails] = useState<boolean>(true);
    const [buttonLoading, setButtonLoading] = useState<boolean>(false);
    const [lookupLoading, setLookupLoading] = useState<boolean>(false);
    const [newUserModal, setNewUserModal] = useState<boolean>(false);
    const [users, setUsers] = useState<any[]>([]);
    const [accounts, setAccounts] = useState<any[]>([]);
    const [filteredAccounts, setFilteredAccounts] = useState<any[]>([]);
    const [userAccounts, setUserAccounts] = useState<any[]>([]);
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [form] = Form.useForm();
    const [advancedFilters, setAdvancedFilters] = useState({});
    const [showAccounts, setShowAccounts] = useState<boolean>(false);
    const statusType = {
        ACTIVE: "Active",
        INACTIVE: "Inactive",
        SUSPENDED: "Suspended",
        STAGED: "Staged",
        LOCKED_OUT: "Locked Out",
        DEACTIVATED: "Deactivated",
    };
    const columns: any = [
        {
            title: 'First Name',
            dataIndex: 'first_name',
        },
        {
            title: 'Last Name',
            dataIndex: 'last_name',
        },
        {
            title: 'Username',
            dataIndex: 'idp_user_name',
        },
        {
            title: "Account Name",
            dataIndex: "account_name"
        },
        {
            title: 'Status',
            dataIndex: 'status',
            fixed: "right"
        }
    ];

    useEffect(() => {
        if (accounts.length === 0) {
            ApiService.get(ApiUrls.getAllAccounts)
                .then((data) => {
                    if (displayError(data)) {
                        setAccounts(data);
                        setFilteredAccounts(data);
                        getUsersList({}, {});
                    } else {
                        setLoadingDetails(false);
                    }
                })
                .catch(() => {
                    openNotification("error", "Unable to load the data");
                    setLoadingDetails(false);
                });
        }
        else {
            getUsersList({}, {})
        }
    }, [window.location.search]);

    const handleAdd = () => {
        form
            .validateFields()
            .then((values) => {
                setButtonLoading(true);
                ApiService.post(ApiUrls.users, values)
                    .then(async (data) => {
                        let successMessage = "";
                        let accountNames: string[] = [];
                        let errorMessages: string[] = [];
                        let firstName = "";

                        data.forEach(item => {
                            if (item.errorCode) {
                                errorMessages.push(item.errorSummary);
                            }
                            else {
                                firstName = item?.first_name;
                                accountNames.push(item?.account_name);
                            }
                        });

                        if (accountNames.length > 0) {
                            successMessage = `Successfully created '${firstName}' user in ${accountNames.join(", ")}`;
                        }

                        if (successMessage !== "") {
                            openNotification('success', successMessage);
                            await getUsersList({ start: 1, limit: 10 });
                            setNewUserModal(false);
                            form.resetFields();
                            handleReset();
                        }

                        if (errorMessages.length > 0) {
                            const uniqueArray = errorMessages.filter((value, index, self) => self.indexOf(value) === index);
                            openNotification("error", `${uniqueArray.join(', ')}`)
                        }
                    })
                    .catch(() => {
                        openNotification('error', 'An error occurred while adding the user.');
                    })
                    .finally(() => setButtonLoading(false));
            })
            .catch(() => {
                openNotification('warning', 'Some fields were left empty.');
                setButtonLoading(false);
            });
    };

    const getUsersList = (params = {}, object = {}) => {
        setLoadingDetails(true);
        if (window.location.search.includes('filters=')) {
            let advancedFiltersData = JSON.parse(decodeURIComponent(window.location.search.split("filters=")[1]));
            object = advancedFiltersData.object
            params = advancedFiltersData.params
            setAdvancedFilters(object)
        }
        Promise.all(([
            (Object.keys(object).length !== 0 && object['filters']) ?
                ApiService.post(ApiUrls.filterUsers, object, params)
                : ApiService.get(ApiUrls.users, params)
        ])).then(([data]) => {
            if (displayError(data)) {
                setPageAndPageSize(setPage, data.page, setPageSize, data.items_per_page)
                setUsers(data.results);
                setTotalItems(data.total_items);
            }
        }).catch(error => {
            console.error('Error: ', error);
            openNotification('error', "Unable to load the data");
        }).finally(() => {
            setLoadingDetails(false);
        });
    }

    const getUpdatedUsersList = () => {
        const params = {
            start: page,
            limit: pageSize
        }
        const pageDetails: any = localStorage.getItem('usersPageParams');
        const parsedPageDetails = JSON.parse(pageDetails);
        if (pageDetails && parsedPageDetails) {
            setPageAndPageSize(setPage, parsedPageDetails.page, setPageSize, parsedPageDetails.pageSize)
            params.start = parsedPageDetails.page;
            params.limit = parsedPageDetails.pageSize;
            localStorage.removeItem('usersPageParams');
        } else {
            params.start = page;
            params.limit = pageSize;
        }
        setAdvancedFilters({})
        getUsersList(params, {});
    }

    const getAccountsByEmail = () => {
        form.validateFields(['email']).then(() => {
            if (form.getFieldValue("email") !== "") {
                setLookupLoading(true);
                ApiService.get(ApiUrls?.userAccountsLookUp, { email: form.getFieldValue("email") })
                    .then(data => {
                        if (displayError(data)) {
                            setUserAccounts(data);
                            if (data?.length === 0) {
                                form.resetFields(["account_uids"]);
                                form.setFieldsValue({ create_user_from_existing: false });
                                setFilteredAccounts(accounts);
                            }
                            else {
                                const accountUids = data.map(user => user.account_uid);
                                setFilteredAccounts(accounts.filter(account => !accountUids.includes(account?.uid)))
                                form.setFieldsValue({ create_user_from_existing: true });

                            }
                            setShowAccounts(true);
                        }
                    })
                    .catch(error => {
                        openNotification("error", "Unable to load the data");
                    })
                    .finally(() => {
                        setLookupLoading(false);
                    });
            }
        }).catch((error) => {
            handleReset()
            openNotification("error", "Please enter a valid email address.");
        });
    };

    const handleEmailChange = (e) => {
        form.setFieldsValue({ email: e.target.value });
    };

    function handleReset() {
        form.resetFields();
        setUserAccounts([]);
        setShowAccounts(false);
    }

    return <>
        <div>
            <div className="content-header">
                Admins
            </div>

        </div>

        <div
        >
            <Button
                type="primary"
                size="large"
                className="margin-bottom-20x"
                onClick={() => setNewUserModal(true)}
            >
                Add New Admin
            </Button>
            <div style={{ float: "right", paddingRight: "10px" }}>
                <FiltersModal
                    pageSize={pageSize}
                    getListByFilter={getUpdatedUsersList}
                    advancedFilters={advancedFilters}
                    onFilterApply={() => null}
                    onResetClick={() => null}
                    type={"users"}
                    accounts={accounts}
                    statusTypes={statusType}
                />
            </div>
        </div>

        <div
            style={{
                width: "100%",
                overflow: "hidden",
            }}
        >
            <Table
                loading={loadingDetails}
                columns={columns}
                dataSource={users}
                bordered={true}
                rowKey="uid"
                onChange={(pagination) => {
                    const pageNumber = pagination.current ?? 1;
                    const limit = pagination.pageSize ?? 10;
                    history.push(
                        window.location.pathname +
                        `?filters=${encodeURIComponent(
                            JSON.stringify({ params: { start: pageNumber, limit: limit }, object: advancedFilters })
                        )}`
                    );
                }}
                pagination={
                    totalItems > 10
                        ? {
                            current: page,
                            pageSize: pageSize,
                            showSizeChanger: totalItems > 20,
                            total: totalItems,
                        }
                        : false
                }
            />
        </div>

        <Modal
            open={newUserModal}
            onCancel={() => {
                handleReset();
                setNewUserModal(false);
            }}
            centered
            style={{ width: "700px" }}
            onOk={handleAdd}
            okText={"Add"}
            okButtonProps={{
                className: "font-size-14px"
            }}
            title={
                <>
                    <label className="common-modal-title">Add admin to Account(s)</label>

                    <Button
                        onClick={handleReset}
                        type="primary"
                        className="font-size-14px"
                        style={{
                            float: "right",
                            marginRight: "35px",
                            marginTop: "6px",
                        }}
                        size="middle"
                    >
                        Clear
                    </Button>
                </>
            }
            closeIcon={<CloseOutlined />}
            confirmLoading={buttonLoading}
            maskClosable={false}
        >
            <div className="content-container">
                <Form
                    form={form}
                    initialValues={initialValues}
                    requiredMark={false}
                    labelCol={{ span: 12 }}
                    labelAlign="left"
                    autoComplete="off"
                    preserve={false}
                    className="margin-top-20px"
                    colon={false}
                >
                    <Form.Item
                        key="email"
                        name="email"
                        className="row-containers"
                        label={
                            <div className="content-policy-key-header">
                                {displayFields["email"]}
                                <Hint title={"Search for a user across all your accounts"} />
                                <span className="mandatory">*</span>
                            </div>
                        }
                        rules={[{ required: true, message: `${displayFields["email"]} is required.` }]}
                        style={{ margin: 0 }}
                    >
                        <Input
                            placeholder={`Please enter ${displayFields["email"]?.toLowerCase()}`}
                            style={{ maxWidth: "275px", width: "100%" }}
                            onChange={handleEmailChange}
                        />
                    </Form.Item>

                    {
                        <div style={{ marginTop: "10px", float: "right" }}>
                            <Button
                                onClick={getAccountsByEmail}
                                className={`font-size-14px${userAccounts?.length !== 0 ? ' margin-bottom-20px' : ''}`}
                                loading={lookupLoading}
                                type="primary"
                            >
                                Accounts lookup
                            </Button>
                        </div>
                    }

                    {userAccounts?.length !== 0 && (
                        <>
                            <Table
                                dataSource={userAccounts}
                                style={{ border: "1px solid #D7D7DC", width: "100%" }}
                                rowKey={"uid"}
                                pagination={false}
                                columns={[
                                    {
                                        title: "Account Name",
                                        dataIndex: "account_name",
                                    },
                                    {
                                        title: "User Role",
                                        dataIndex: "user_role",
                                        render: (text, record) => {
                                            return displayRoles[text] ?? text;
                                        },
                                    },
                                ]}
                                bordered
                            />
                        </>
                    )}

                    {showAccounts && (
                        filteredAccounts?.length === 0 ? (
                            <div className="message-header">
                                The user is already added to all the accounts.
                            </div>
                        ) : (
                            <Form.Item
                                key={"account_uids"}
                                name={"account_uids"}
                                label={
                                    <div className="content-policy-key-header">
                                        {displayFields["account_uids"]}
                                        <Hint title={"Select accounts to assign admin access. If the user doesn’t exist, they will be created."} />
                                        <span className="mandatory">*</span>
                                    </div>
                                }
                                className="row-containers"
                                rules={[{ required: true, message: `Please select ${displayFields["account_uids"]?.toLowerCase()}` }]}
                                style={{ padding: "10px 0", margin: userAccounts?.length === 0 ? "55px 0 0 0" : 0 }}
                            >
                                <Select
                                    mode="multiple"
                                    style={{ maxWidth: "275px", width: "100%" }}
                                    placeholder={"Please select an account"}
                                    //@ts-ignore
                                    filterOption={(value, option) => option?.children?.toLowerCase()?.includes(value?.toLowerCase())}
                                >
                                    {filteredAccounts.map((account) => (
                                        <Select.Option key={account?.uid} value={account?.uid}>
                                            {account?.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )
                    )}

                    <Form.Item shouldUpdate={(prev, curr) => prev !== curr} noStyle>
                        {({ getFieldValue }) => {
                            const accountsCount = getFieldValue(["account_uids"]);

                            return accountsCount?.length > 0 && userAccounts?.length !== 0 && (
                                <Form.Item
                                    key={"create_user_from_existing"}
                                    name={"create_user_from_existing"}
                                    label={<div className="content-policy-key-header">{displayFields["create_user_from_existing"]}</div>}
                                    className="row-containers-checkbox"
                                    style={{ padding: "10px 0", margin: 0 }}
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>
                            );
                        }}
                    </Form.Item>

                    <Form.Item shouldUpdate={(prev, curr) => prev !== curr} noStyle>
                        {({ getFieldValue }) => {
                            const showFields = getFieldValue(["create_user_from_existing"]);

                            return ["first_name", "last_name", "display_name", "idp_user_name"].map((key) => (
                                !showFields && (
                                    <Form.Item
                                        key={key}
                                        name={key}
                                        style={{ padding: "10px 0", margin: 0 }}
                                        className="row-containers"
                                        label={
                                            <div className="content-policy-key-header">
                                                {displayFields[key]}<span className="mandatory">*</span>
                                            </div>
                                        }
                                        rules={[{ required: true, message: `${displayFields[key]} is required.` }]}
                                    >
                                        <Input
                                            placeholder={`Please enter ${displayFields[key]?.toLowerCase()}`}
                                            style={{ maxWidth: "275px", width: "100%" }}
                                        />
                                    </Form.Item>
                                )
                            ));
                        }}
                    </Form.Item>
                </Form>
            </div>
        </Modal>
    </>
}

export default Users;